import { CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { storageUserKey } from './auth-context.constants';

type User = {
  email: string;
  name: string;
  picture: string;
  token: string;
};

interface AuthContextType {
  user: User | null;
  isAuthReady: boolean;
  signIn: (resp: CredentialResponse) => void;
  signOut: () => void;
}

interface GoogleJwtPayload {
  email: string;
  name: string;
  picture: string;
  [key: string]: unknown;
}

const createUserData = (decoded: GoogleJwtPayload, token: string) => ({
  email: decoded.email,
  name: decoded.name,
  picture: decoded.picture,
  token,
});

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

function getStorage() {
  if (typeof window !== 'undefined') {
    return window.sessionStorage;
  }

  return {
    getItem: () => null,
    setItem: () => null,
    removeItem: () => null,
  };
}

function loadUserFromStorage(): User | null {
  try {
    const storedUser = getStorage().getItem(storageUserKey);
    return storedUser ? JSON.parse(storedUser) : null;
  } catch (error) {
    console.error('Error reading user from storage:', error);
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    setUser(loadUserFromStorage());
    setIsAuthReady(true);
  }, []);

  const signIn = useCallback(async (response: CredentialResponse) => {
    try {
      const token = response.credential;
      if (!token) {
        throw new Error('No credential received');
      }

      const decoded = jwtDecode<GoogleJwtPayload>(token);
      const userData = createUserData(decoded, token);

      setUser(userData);
      getStorage().setItem(storageUserKey, JSON.stringify(userData));
    } catch (error) {
      console.error('Error during sign in:', error);
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    setUser(null);
    getStorage().removeItem(storageUserKey);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isAuthReady,
      signIn,
      signOut,
    }),
    [isAuthReady, signIn, signOut, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
